import { Product } from '@interfaces/models/product';
import {
  BuyingOffer,
  BuyingOfferProduct,
  BuyingOffersResponse,
  NegotiationLastAction,
} from '@interfaces/models/negotiation/domain/BuyingOffer';
import { ApiResponse } from '@interfaces/api';
import {
  BuyingOfferDTO,
  BuyingOfferProductDTO,
  NegotiationLastActionDTO,
} from '@interfaces/models/negotiation/dtos/BuyingOfferDTO';
import { Negotiation } from '@interfaces/models/negotiation/negotiation';
import { Price } from '@interfaces/models/price';
import logger from '@helpers/utils/logger/client';
import { datadogRum } from '@datadog/browser-rum-slim';
import { mapNegotiationStatusFromDTO } from './negotiationOfferStatusMapper';

/**
 * Maps a BuyingOffer Product to generic Product type (minimum required fields)
 */
export const mapBuyingOffersProductToBaseProduct = (negotiationProduct: BuyingOfferProduct): Product => {
  return {
    id: String(negotiationProduct.id),
    type: 'product',
    name: negotiationProduct.headline,
    price: negotiationProduct.price,
  };
};

/**
 * Maps a NegotiationLastActionDTO to a NegotiationLastAction domain type
 */

const mapNegotiationLastActionFromDTO = (dto: NegotiationLastActionDTO): NegotiationLastAction => {
  return {
    originator: dto.originator,
    action: dto.action,
    offerFormatted: dto.offerFormatted,
    actionNote: dto.actionNote,
  };
};

/**
 * Maps a BuyingOfferProductDTO to a BuyingOfferProduct domain type
 */
const mapBuyingOfferProductFromDTO = (dto: BuyingOfferProductDTO): BuyingOfferProduct => {
  return {
    id: dto.id,
    headline: dto.headline,
    subHeadline: dto.subHeadline,
    picturePath: dto.picturePath,
    price: { ...dto.price },
    buyerFeesIncludes: {
      crossBorderFees: dto.buyerFeesIncludes.crossBorderFees,
      mandatoryAuthentication: dto.buyerFeesIncludes.mandatoryAuthentication,
    },
    priceDetails: {
      itemPrice: { ...dto.priceDetails.itemPrice },
      buyerFees: { ...dto.priceDetails.buyerFees },
      buyerTotal: { ...dto.priceDetails.buyerTotal },
    },
    locationName: dto.locationName,
    path: dto.path,
  };
};

/**
 * Maps a BuyingOfferDTO to a BuyingOffer domain type
 */
const mapBuyingOfferFromDTO = (dto: BuyingOfferDTO): BuyingOffer => {
  return {
    id: dto.id,
    negotiationStatus: mapNegotiationStatusFromDTO(dto.negotiationStatus),
    lastAction: mapNegotiationLastActionFromDTO(dto.lastAction),
    product: mapBuyingOfferProductFromDTO(dto.product),
  };
};

/**
 * Maps a BuyingOffersResponseDTO to a BuyingOffersResponse domain type
 */
export const mapBuyingOffersResponseFromDTO = (dto: ApiResponse<BuyingOfferDTO[]>): BuyingOffersResponse => {
  return {
    meta: dto.meta,
    data: dto.data.map((offer) => mapBuyingOfferFromDTO(offer)),
  };
};

/**
 * Maps a Negotiation to a BuyingOfferProduct domain type
 */
export const mapNegotiationToBuyingOfferProduct = (negotiation: Negotiation): BuyingOfferProduct => {
  let err: Error | null = null;

  if (!negotiation) {
    err = new Error('[mapNegotiationToBuyingOfferProduct]: Cannot map null or undefined negotiation');
  } else if (!negotiation.product) {
    err = new Error('[mapNegotiationToBuyingOfferProduct]: Cannot map negotiation without product');
  } else if (!negotiation.pricingBreakdown) {
    err = new Error('[mapNegotiationToBuyingOfferProduct]: Cannot map negotiation without pricingBreakdown');
  }
  if (err) {
    datadogRum.addError(err);
    logger.error(err);
  }

  return {
    id: Number(negotiation.product.id),
    headline: negotiation.product.brand?.name,
    subHeadline: negotiation.product.name,
    picturePath: negotiation.product.pictures?.[0]?.path,
    path: negotiation.product.path,
    price: negotiation.pricingBreakdown.fullPrice,
    buyerFeesIncludes: {
      crossBorderFees: negotiation.pricingBreakdown.buyerFees?.hasCrossBorderFees,
      mandatoryAuthentication: negotiation.pricingBreakdown.buyerFees?.includesAuthenticationFees,
    },
    priceDetails: {
      itemPrice: negotiation.pricingBreakdown.fullPrice,
      buyerFees: negotiation.pricingBreakdown.buyerFees?.amount as Price,
      buyerTotal: negotiation.product.pricingBreakdown?.sellerPrice,
    },
    locationName: negotiation.productLocationNameLocalized,
  };
};

/**
 * Maps a Negotiation to a BuyingOffer domain type
 */
export const mapNegotiationToBuyingOffer = (negotiation: Negotiation): BuyingOffer => {
  let err: Error | null = null;

  if (!negotiation) {
    err = new Error('[mapNegotiationToBuyingOffer]: Cannot map null or undefined negotiation');
  } else if (!negotiation.lastEvent) {
    err = new Error('[mapNegotiationToBuyingOffer]: Cannot map negotiation without lastEvent');
  }
  if (err) {
    datadogRum.addError(err);
    logger.error(err);
  }

  const isLastEventBuyer = negotiation.lastEvent.createdBy === 'buyer';

  return {
    id: negotiation.id,
    negotiationStatus: {
      callerSubMessage: negotiation.userSubMessage,
      isAccepted: negotiation.isAccepted,
      isClosed: negotiation.isClosed,
      // caller could be buyer or seller
      callerRemainingActionsNumber: isLastEventBuyer
        ? negotiation.sellerRemainingOffers
        : negotiation.buyerRemainingOffers,
      callerPossibleActions: negotiation.nextActions,
    },
    lastAction: {
      originator: negotiation.lastEvent.createdBy,
      action: negotiation.lastEvent.action,
      offerFormatted: negotiation.lastEvent.price?.formatted,
      actionNote: negotiation.lastEvent.userMessage,
    },
    product: mapNegotiationToBuyingOfferProduct(negotiation),
  };
};
